/* eslint-disable @next/next/no-img-element */
/* istanbul ignore file */

// deps
import { Box, Grid, GridItem, Select, VStack } from "@chakra-ui/react";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import ReactCrop from "react-image-crop";
import { useIntl } from "react-intl";
import FormController from "../../ReactHookForm/FormController";

// helpers

const RATIO_LIST = [
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "3:2", value: 3 / 2 },
  { label: "1:1", value: 1 },
];

/**
 * Retourne la taille maximale de l'image appropriée.
 * @param {object} param0
 * @param {number} [param0.ratio]
 * @param {number} param0.naturalWidth
 * @param {number} param0.naturalHeight
 * @return {{width: number, height: number}}
 */
function getBestDefaultSizes({ ratio, naturalWidth, naturalHeight }) {
  const imageRatio = naturalWidth / naturalHeight;
  const size = {
    width: 100,
    height: 100,
  };
  if (ratio && ratio !== 1) {
    // we determine if the limiting factor is the width or the height
    if (imageRatio > ratio) {
      // the limiting factor is the height
      size.height = 100;
      size.width = (100 * ratio) / imageRatio;
    }
    if (imageRatio < ratio) {
      // the limiting factor is the width
      size.width = 100;
      size.height = (100 * imageRatio) / ratio;
    }
  }
  return size;
}

/**
 * @param {object} param0
 * @param {import("react-hook-form").UseFormReturn} param0.form
 * @param {*} param0.value
 * @param {*} param0.crop
 * @param {"ratio" | "width" | "height"} param0.type
 * @param {number} param0.naturalWidth
 * @param {number} [param0.width]
 * @param {number} param0.naturalHeight
 * @param {string} param0.transformationsName
 * @param {number} param0.ratio
 * @param {string} param0.formatName
 * @return {object}
 */
function setNewValues({
  form,
  value,
  crop,
  type,
  naturalWidth,
  width,
  naturalHeight,
  transformationsName,
  ratio,
  formatName,
}) {
  switch (type) {
    case "ratio": {
      const ratio = value;

      const newHeight = Math.round((width ?? 0) / ratio);

      form.setValue(
        `${transformationsName}${formatName ? `.${formatName}` : ""}.height`,
        newHeight,
        { shouldDirty: true },
      );
      form.setValue(
        `${transformationsName}${formatName ? `.${formatName}` : ""}.ratio`,
        ratio,
        { shouldDirty: true },
      );

      const newCrop = {
        ...crop,
        height: (100 * newHeight) / naturalHeight,
      };

      return { newCrop };
    }

    case "width": {
      const newWidth = value;

      if (isNaN(newWidth)) {
        form.setValue(
          `${transformationsName}${formatName ? `.${formatName}` : ""}.width`,
          "",
          { shouldDirty: true },
        );

        const newCrop = {
          width: undefined,
        };

        if (ratio) {
          newCrop.height = undefined;

          form.setValue(
            `${transformationsName}${
              formatName ? `.${formatName}` : ""
            }.height`,
            "",
            { shouldDirty: true },
          );
        }

        return {
          newCrop,
        };
      }

      form.setValue(
        `${transformationsName}${formatName ? `.${formatName}` : ""}.width`,
        newWidth,
        { shouldDirty: true },
      );

      const newCrop = {
        ...crop,
        width: (100 * newWidth) / naturalWidth,
      };

      if (ratio) {
        const newHeight = Math.round(newWidth / ratio);

        form.setValue(
          `${transformationsName}${formatName ? `.${formatName}` : ""}.height`,
          newHeight,
          { shouldDirty: true },
        );

        newCrop.height = (100 * newHeight) / naturalHeight;
      }

      return { newCrop };
    }

    case "height": {
      const newHeight = value;

      if (isNaN(newHeight)) {
        form.setValue(
          `${transformationsName}${formatName ? `.${formatName}` : ""}.height`,
          "",
          { shouldDirty: true },
        );

        const newCrop = {
          height: undefined,
        };

        if (ratio) {
          newCrop.width = undefined;

          form.setValue(
            `${transformationsName}${formatName ? `.${formatName}` : ""}.width`,
            "",
            { shouldDirty: true },
          );
        }

        return {
          newCrop,
        };
      }

      const newCrop = {
        ...crop,
        height: (100 * newHeight) / naturalHeight,
      };

      form.setValue(
        `${transformationsName}${formatName ? `.${formatName}` : ""}.height`,
        newHeight,
        { shouldDirty: true },
      );

      if (ratio) {
        const newWidth = Math.round(newHeight * ratio);

        form.setValue(
          `${transformationsName}${formatName ? `.${formatName}` : ""}.width`,
          newWidth,
          { shouldDirty: true },
        );

        newCrop.width = (100 * newWidth) / naturalWidth;
      }

      return {
        newCrop,
      };
    }
  }
}

function ImagesTransformRHF(props) {
  const {
    image,
    transformationsName,
    formatName,
    variant,
    format,
    circularCrop,
    isDisabled,
  } = props;

  const intl = useIntl();

  /** @type {import("react").MutableRefObject<HTMLImageElement | null>} */
  const $img = useRef(null);

  const { min_width: minWidth, min_height: minHeight } = format;

  const hasRatioField = !format?.ratio;

  const form = useFormContext();
  const { watch, setValue } = form;

  const formCrop = watch(
    `${transformationsName}${formatName ? `.${formatName}` : ""}`,
  );

  const ratio = format?.ratio ?? formCrop?.ratio;

  const [crop, setCrop] = useState(
    /** @type {import("react-image-crop").Crop | undefined} */ (undefined),
  );

  /**
   * Défini les valeurs du crop.
   * @param {object} param0
   * @param {import("react-image-crop").Crop} param0.crop
   */
  function setFormValues({ crop }) {
    const naturalWidth = $img.current?.naturalWidth ?? 0;
    const naturalHeight = $img.current?.naturalHeight ?? 0;

    const newWidth = Math.max(
      minWidth,
      Math.round(naturalWidth * (crop.width / 100)),
    );
    const newHeight = Math.max(
      minHeight,
      Math.round(naturalHeight * (crop.height / 100)),
    );

    setValue(
      `${transformationsName}${formatName ? `.${formatName}` : ""}.x`,
      Math.round(naturalWidth * (crop.x / 100)),
      { shouldDirty: true },
    );
    setValue(
      `${transformationsName}${formatName ? `.${formatName}` : ""}.y`,
      Math.round(naturalHeight * (crop.y / 100)),
      { shouldDirty: true },
    );
    setValue(
      `${transformationsName}${formatName ? `.${formatName}` : ""}.width`,
      newWidth,
      { shouldDirty: true },
    );
    setValue(
      `${transformationsName}${formatName ? `.${formatName}` : ""}.height`,
      newHeight,
      { shouldDirty: true },
    );

    const { newCrop } = setNewValues({
      form,
      value: newWidth,
      type: "width",
      naturalWidth,
      naturalHeight,
      crop: crop,
      formatName,
      ratio,
      transformationsName,
    });

    setCrop(newCrop);
  }

  const widthClientNaturalRatio =
    ($img.current?.clientWidth ?? 1) / ($img.current?.naturalWidth ?? 1);
  const heightClientNaturalRatio =
    ($img.current?.clientHeight ?? 1) / ($img.current?.naturalHeight ?? 1);

  /** Détermine si l'image courante est l'image initiale */
  const initial = useRef(true);

  return (
    <Grid
      templateColumns={
        "tight" === variant ? { md: "repeat(7, 1fr)" } : undefined
      }
      gap="1rem">
      <GridItem gridColumn={"tight" === variant ? "1 / span 5" : undefined}>
        <Box display="flex" justifyContent="center" w="full">
          <ReactCrop
            disabled={isDisabled}
            crop={
              crop ?? {
                x: 0,
                y: 0,
                width: 100,
                height: 100,
                unit: "%",
              }
            }
            minWidth={minWidth * widthClientNaturalRatio}
            maxWidth={$img.current?.clientWidth}
            minHeight={minHeight * heightClientNaturalRatio}
            maxHeight={$img.current?.clientHeight}
            aspect={ratio}
            circularCrop={circularCrop}
            onChange={function (_, newCrop) {
              if ($img.current && crop) {
                setCrop(newCrop);
              }
              setCrop(newCrop);
            }}
            onComplete={function (_, newCrop) {
              if ($img.current && crop) {
                setFormValues({ crop: newCrop });
              }
            }}>
            <img
              alt={intl.formatMessage({
                defaultMessage: "Image à redimensionner",
              })}
              src={image}
              onLoad={function (event) {
                // @ts-ignore
                $img.current = event.target;

                const naturalWidth = $img.current?.naturalWidth ?? 0;
                const naturalHeight = $img.current?.naturalHeight ?? 0;

                if (formCrop && initial.current) {
                  /** @type {import("react-image-crop").Crop} */
                  const newCrop = {
                    x: (100 * formCrop.x) / naturalWidth,
                    y: (100 * formCrop.y) / naturalHeight,
                    width: (100 * formCrop.width) / naturalWidth,
                    height: (100 * formCrop.height) / naturalHeight,
                    unit: "%",
                  };

                  setCrop(newCrop);

                  setFormValues({ crop: newCrop });

                  initial.current = false;
                } else {
                  const { width, height } = getBestDefaultSizes({
                    ratio,
                    naturalWidth,
                    naturalHeight,
                  });

                  /** @type {import("react-image-crop").Crop} */
                  const newCrop = {
                    x: 0,
                    y: 0,
                    width,
                    height,
                    unit: "%",
                  };

                  setCrop(newCrop);

                  setFormValues({ crop: newCrop });
                }
              }}
            />
          </ReactCrop>
        </Box>
      </GridItem>

      <GridItem gridColumn={"tight" === variant ? "span 2" : undefined}>
        <VStack spacing="1rem" alignItems="stretch">
          {hasRatioField && (
            <FormController
              name={`${transformationsName}${
                formatName ? `.${formatName}` : ""
              }.ratio`}
              label={intl.formatMessage({
                defaultMessage: "Ratio",
              })}
              rules={{
                onChange(event) {
                  const naturalWidth = $img.current?.naturalWidth ?? 0;
                  const naturalHeight = $img.current?.naturalHeight ?? 0;

                  const newRatio = event.target["value"];

                  if ("" !== newRatio) {
                    const { newCrop } = setNewValues({
                      form,
                      value: newRatio,
                      type: "ratio",
                      naturalWidth,
                      naturalHeight,
                      crop: formCrop,
                      formatName,
                      ratio: newRatio,
                      transformationsName,
                      width: formCrop?.width,
                    });
                    setCrop(newCrop);
                  } else {
                    form.setValue(
                      `${transformationsName}${
                        formatName ? `.${formatName}` : ""
                      }.ratio`,
                      undefined,
                      { shouldDirty: true },
                    );
                  }
                },
              }}>
              <Select>
                {RATIO_LIST.map(function (ratio) {
                  return (
                    <option key={ratio.label} value={ratio.value}>
                      {ratio.label}
                    </option>
                  );
                })}

                <option value="">
                  {intl.formatMessage({
                    defaultMessage: "Libre",
                  })}
                </option>
              </Select>
            </FormController>
          )}

          <FormController
            name={`${transformationsName}${
              formatName ? `.${formatName}` : ""
            }.width`}
            label={intl.formatMessage({
              defaultMessage: "Largeur",
            })}
            type="number-input"
            inputProps={{
              min: minWidth,
              max: $img.current?.naturalWidth,
              step: 1,
            }}
            rules={{
              min: minWidth,
              max: $img.current?.naturalWidth,
              onChange(event) {
                const naturalWidth = $img.current?.naturalWidth ?? 0;
                const naturalHeight = $img.current?.naturalHeight ?? 0;

                const newWidth = event.target["value"];

                const { newCrop } = setNewValues({
                  form,
                  value: newWidth,
                  type: "width",
                  naturalWidth,
                  naturalHeight,
                  crop: crop,
                  formatName,
                  ratio,
                  transformationsName,
                });

                setCrop(newCrop);
              },
            }}
            helperText={
              minWidth
                ? intl.formatMessage(
                    {
                      defaultMessage: "Largeur minimale : {minWidth}px",
                    },
                    {
                      minWidth,
                    },
                  )
                : undefined
            }
          />

          <FormController
            name={`${transformationsName}${
              formatName ? `.${formatName}` : ""
            }.height`}
            label={intl.formatMessage({
              defaultMessage: "Hauteur",
            })}
            type="number-input"
            inputProps={{
              min: minHeight,
              max: $img.current?.naturalHeight,
              step: 1,
            }}
            rules={{
              min: minHeight,
              max: $img.current?.naturalHeight,
              onChange(event) {
                const naturalWidth = $img.current?.naturalWidth ?? 0;
                const naturalHeight = $img.current?.naturalHeight ?? 0;

                const newHeight = event.target["value"];

                const { newCrop } = setNewValues({
                  form,
                  value: newHeight,
                  type: "width",
                  naturalWidth,
                  naturalHeight,
                  crop: crop,
                  formatName,
                  ratio,
                  transformationsName,
                });

                setCrop(newCrop);
              },
            }}
            helperText={
              minHeight
                ? intl.formatMessage(
                    {
                      defaultMessage: "Hauteur minimale : {minHeight}px",
                    },
                    {
                      minHeight,
                    },
                  )
                : undefined
            }
          />
        </VStack>
      </GridItem>
    </Grid>
  );
}

ImagesTransformRHF.propTypes = {
  variant: PropTypes.oneOf(["tight", "expanded"]),
  form: PropTypes.object,
  circularCrop: PropTypes.bool,
  image: PropTypes.any,
  transformationsName: PropTypes.string,
  formatName: PropTypes.string,
  format: PropTypes.object,
  isDisabled: PropTypes.bool,
};

export default ImagesTransformRHF;
